import {
  Box,
  Button,
  CircularProgress,
  Modal,
  Paper,
  ThemeProvider,
  Typography,
} from "@material-ui/core";
import Zoom from "@material-ui/core/Zoom";
import { DeleteOutline } from "@material-ui/icons";
import FileCopyIcon from "@material-ui/icons/FileCopy";
import MaterialTable from "material-table";
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { DisplayMessage } from "../../../../services/helpers";
import { ModalContent } from "../../../../styles/box/styles";
import { CancelButton, OkButton } from "../../../../styles/button/styles";
import { useStyles } from "../../../../styles/styles";
import { TableIcons } from "../../../../utils/TableIcons";
import { LocalizationTheme } from "../../../../utils/utils";
import {
  copyDisclaimer,
  deleteDisclaimer,
  getDisclaimers,
} from "../Disclaimers/services/disclaimersApisService";
import { sortTableStringsNotCaseSensitive } from "../../../../utils/tableSorting";
import OutletsDropdown from "../../../Common/Dropdowns/OutletsDropdown";
import LanguageDropdown from "../../../Common/Dropdowns/LanguageDropdown";

const DisclaimersTable = (props) => {
  const { resources, accessibleMoboOutlets, outletId, refreshGrid } = props;
  const [selectedOutlet, setSelectedOutlet] = useState(outletId ? outletId : 0);
  const [disclaimers, setDisclaimers] = useState([]);
  const [requestInProgress, setRequestInProgress] = useState(false);
  const [fetchingData, setFetchingData] = useState(true);
  const [copyModalOpen, setCopyModalOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [copiedDisclaimer, setCopiedDisclaimer] = useState({
    id: 0,
    moboOutlet: "",
    type: "",
    language: "",
  });
  const [copyToOutlet, setCopyToOutlet] = useState(0);
  const [copyToLanguage, setCopyToLanguage] = useState("nl");
  const [deleteDisclaimerId, setDeleteDisclaimerId] = useState(0);
  const [refresh, setRefresh] = useState(false);

  const classes = useStyles();

  const tableSettings = {};

  tableSettings.actions = [
    {
      icon: () => <DeleteOutline />,
      tooltip: resources["Delete"],
      onClick: (event, rowData) => handleDelete(rowData.id),
    },

    {
      icon: () => <FileCopyIcon />,
      tooltip: resources["Copy"],
      onClick: (event, rowData) => handleCopy(rowData),
    },
  ];

  tableSettings.columns = [
    {
      title: resources["Outlet"],
      field: "moboOutlet",
      cellStyle: {
        width: "25%",
        minWidth: "150px",
      },
      headerStyle: {
        width: "25%",
        minWidth: "150px",
      },
      customSort: (a, b, type) => sortTableStringsNotCaseSensitive(a.moboOutlet, b.moboOutlet)
    },
    {
      title: resources["Type"],
      field: "type",
      cellStyle: {
        width: "55%",
        minWidth: "300px",
      },
      headerStyle: {
        width: "55%",
        minWidth: "300px",
      },
      customSort: (a, b) => sortTableStringsNotCaseSensitive(a.type, b.type)
    },
    {
      title: resources["Language"],
      field: "language",
      cellStyle: {
        width: "10%",
        minWidth: "100px",
      },
      headerStyle: {
        width: "10%",
        minWidth: "100px",
      },
      customSort: (a, b) => sortTableStringsNotCaseSensitive(a.language, b.language)
    },
  ];

  const handleDelete = (id) => {
    setDeleteDisclaimerId(id);
    setDeleteModalOpen(true);
  };

  const handleCloseDelete = () => {
    setDeleteDisclaimerId(0);
    setDeleteModalOpen(false);
  };

  const handleSubmitDelete = async () => {
    setRequestInProgress(true);

    var result = await deleteDisclaimer(deleteDisclaimerId);

    setRequestInProgress(false);
    handleCloseDelete();

    if (result === true) {
      DisplayMessage(resources["DisclaimerWasDeletedWithSuccess"], "success");
      setRefresh(true);
    }
  };

  const handleCopy = (data) => {
    setCopiedDisclaimer(data);
    setCopyModalOpen(true);
  };

  const handleCloseCopy = () => {
    setCopiedDisclaimer({ id: 0, moboOutlet: "", type: "", language: "" });
    setCopyToLanguage("nl");
    setCopyToOutlet(0);
    setCopyModalOpen(false);
  };

  const handleSubmitCopy = async () => {
    setRequestInProgress(true);
    var result = await copyDisclaimer(
      JSON.stringify({
        sourceId: copiedDisclaimer.id,
        moboOutletId: copyToOutlet,
        language: copyToLanguage,
      })
    );

    setRequestInProgress(false);
    handleCloseCopy();

    if (result !== undefined) {
      DisplayMessage(resources["DisclaimerWasCopiedWithSuccess"], "success");
      if (copyToOutlet === selectedOutlet || selectedOutlet === 0) {
        setDisclaimers([
          ...disclaimers,
          {
            id: result.id,
            moboOutlet: getMoboOutletDescription(result.moboOutletId),
            type: getTypeDescription(result.type),
            language: result.language,
          },
        ]);
      }
    }
  };

  const getMoboOutletDescription = (moboOutletId) => {
    let moboOutlet = accessibleMoboOutlets.find(
      (outlet) => outlet.id === moboOutletId
    );
    return moboOutlet?.description ?? null;
  };

  const getTypeDescription = (type) => {
    switch (type) {
      case 1:
        return resources["DisclaimerType1"];
      case 2:
        return resources["DisclaimerType2"];
      case 3:
        return resources["DisclaimerType3"];
      case 4:
        return resources["DisclaimerType4"];
      default:
        return resources["Unknown"];
    }
  };

  const getData = (outletId) => {
    var fetchedDisclaimers = [];

    setFetchingData(true);
    getDisclaimers(outletId).then((data) => {
      if (data === undefined) {
        setDisclaimers([]);
        setFetchingData(false);

        return;
      }
      if (data.length === 0) {
        setDisclaimers([]);
      } else {
        data.sort((el1, el2) => el1.id - el2.id).forEach((item) => {
          fetchedDisclaimers.push({
            id: item.id,
            moboOutlet: getMoboOutletDescription(item.moboOutletId),
            type: getTypeDescription(item.type),
            language: item.language,
          });
        });
        setDisclaimers(fetchedDisclaimers);
      }
      setFetchingData(false);
    });
  };

  useEffect(() => {
    if (refreshGrid !== true) {
      getData(selectedOutlet);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (refresh === true) {
      getData(selectedOutlet);
      setRefresh(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refresh]);

  useEffect(() => {
    if (refreshGrid === true) {
      getData(selectedOutlet);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshGrid]);

  const handleChangeOutlet = (event) => {
    setSelectedOutlet(parseInt(event.target.value));
    setRefresh(true);
  };

  const handleChangeOutletToCopy = (event) => {
    setCopyToOutlet(parseInt(event.target.value));
  };

  const handleChangeLanguage = (event) => {
    setCopyToLanguage(event.target.value);
  };

  const generateInfo = () => {
    return (
      <>
        <Box display="flex">
          <Box marginRight="12px" minWidth="100px">
            <Typography> {resources["Outlet"]}: </Typography>
          </Box>
          <Box sx={{ wordBreak: "break-word" }}>
            <Typography> {copiedDisclaimer.moboOutlet} </Typography>
          </Box>
        </Box>
        <Box display="flex">
          <Box marginRight="12px" minWidth="100px">
            <Typography> {resources["Type"]}: </Typography>
          </Box>
          <Box sx={{ wordBreak: "break-word" }}>
            <Typography> {copiedDisclaimer.type} </Typography>
          </Box>
        </Box>
        <Box display="flex">
          <Box marginRight="12px" minWidth="100px">
            <Typography> {resources["Language"]}: </Typography>
          </Box>
          <Box sx={{ wordBreak: "break-word" }}>
            <Typography> {copiedDisclaimer.language} </Typography>
          </Box>
        </Box>{" "}
      </>
    );
  };

  return (
    <Box>
      <Box className="position-relative py-4">
        <Typography variant="h3">{resources["Disclaimers"]}</Typography>
        <Box
          className={
            "offcentered-loader " + (fetchingData ? "fadeIn" : "fadeOut")
          }
        >
          <CircularProgress />
        </Box>
        <Box className={"mt-4 " + (fetchingData ? "fadeOut" : "fadeIn")}>
          <Box>
            <OutletsDropdown
              resources={resources}
              outlets={accessibleMoboOutlets}
              outlet={selectedOutlet ?? 0}
              onOutletChange={handleChangeOutlet}
              id="outlet-dropdown-grid"
              containerClass="main-dropdown"
            />
            <Box display="flex" justifyContent="flex-end" sx={{ mb: "10px" }}>
              <Link
                to={{
                  pathname: "/disclaimers/new",
                  state: { outletId: selectedOutlet },
                }}
                style={{ textDecoration: "none" }}
              >
                <Button id="add-Disclaimer" variant="contained" color="primary">
                  <i
                    className="fas fa-plus mr-2"
                    title={resources["AddNewDisclaimer"]}
                  />
                  {resources["AddNewDisclaimer"]}
                </Button>
              </Link>
            </Box>
          </Box>
          <Box style={{ clear: "both" }}></Box>
          <Paper elevation={3} className="p-3 mt-3">
            <ThemeProvider theme={LocalizationTheme()}>
              <MaterialTable
                icons={TableIcons}
                title={""}
                localization={{
                  header: { actions: resources["Actions"] },
                  body: {
                    emptyDataSourceMessage: resources["NoRecordsToBeDisplayed"],
                    deleteTooltip: resources["DeleteTooltip"],
                    editTooltip: resources["EditTooltip"],
                  },
                  toolbar: {
                    searchPlaceholder: resources["Search"],
                    searchTooltip: resources["Search"],
                  },
                  pagination: {
                    firstTooltip: resources["FirstTooltip"],
                    lastTooltip: resources["LastTooltip"],

                    previousTooltip: resources["PreviousTooltip"],
                    nextTooltip: resources["NextTooltip"],

                    labelRowsPerPage: resources["LabelRowsPerPage"],
                    labelRowsSelect: resources["LabelRowsSelect"],
                  },
                }}
                data={disclaimers}
                columns={tableSettings.columns}
                actions={tableSettings.actions}
                options={{
                  emptyRowsWhenPaging: false,
                  pageSize: 10,
                  pageSizeOptions: [10, 15, 20],
                }}
              />
            </ThemeProvider>
          </Paper>
        </Box>
        <Modal
          BackdropProps={{ style: { backgroundColor: "#0000004D" } }}
          open={copyModalOpen}
          onClose={handleCloseCopy}
          className={classes.modal}
        >
          <Zoom in={copyModalOpen} timeout={350}>
            <ModalContent
              className="bounce-in"
              style={{
                transform: copyModalOpen ? "scale(1)" : "scale(0.3)",
              }}
            >
              <Box marginY={2}>
                <Typography>{resources["CopyDisclaimer"]}</Typography>
              </Box>
              <Box marginBottom={2}>{generateInfo()}</Box>
              <Box marginBottom={2.5}>
                <Typography>{resources["To"]}:</Typography>
              </Box>
              <Box display="flex" flexDirection="column" marginBottom={2}>
                <Box marginBottom={2}>
                  <OutletsDropdown
                    resources={resources}
                    outlets={accessibleMoboOutlets}
                    outlet={copyToOutlet}
                    onOutletChange={handleChangeOutletToCopy}
                    id="outlet-dropdown-copy"
                    required
                    defaultValue={resources["SelectOutlet"]}
                    helperText={resources["SelectionOfOutletIsMandatory"]}
                  />
                </Box>
                <Box>
                  <LanguageDropdown
                    resources={resources}
                    language={copyToLanguage}
                    onLanguageChange={handleChangeLanguage}
                    id="language-dropdown-copy"
                  />
                </Box>
              </Box>
              <Box display="flex" justifyContent="flex-end" marginTop={3}>
                <CancelButton id="btnCancelCopy" onClick={handleCloseCopy}>
                  {resources["Cancel"]}
                </CancelButton>
                <OkButton
                  variant="contained"
                  id="btnCopy"
                  disabled={
                    requestInProgress ||
                    copyToLanguage === "0" ||
                    copyToOutlet === 0
                  }
                  onClick={handleSubmitCopy}
                >
                  {resources["Ok"]}
                </OkButton>
              </Box>
              <span className="rodal-close" onClick={handleCloseCopy}></span>
            </ModalContent>
          </Zoom>
        </Modal>
        <Modal
          BackdropProps={{ style: { backgroundColor: "#0000004D" } }}
          open={deleteModalOpen}
          onClose={handleCloseDelete}
          className={classes.modal}
        >
          <Zoom in={deleteModalOpen} timeout={350}>
            <ModalContent
              className="bounce-in"
              style={{
                transform: deleteModalOpen ? "scale(1)" : "scale(0.3)",
              }}
            >
              <Box
                mt={2.5}
                padding={2.5}
                minHeight={118}
                alignContent={"center"}
              >
                <Typography align="center">
                  {resources["AreYouSureYouWantToDeleteDisclaimer"]}
                </Typography>
              </Box>
              <Box display="flex" justifyContent="flex-end" padding={1.5}>
                <CancelButton id="btnCancelDelete" onClick={handleCloseDelete}>
                  {resources["Cancel"]}
                </CancelButton>
                <OkButton
                  id="btnDelete"
                  onClick={handleSubmitDelete}
                  disabled={requestInProgress}
                >
                  {resources["Yes"]}
                </OkButton>
              </Box>
              <span className="rodal-close" onClick={handleCloseDelete}></span>
            </ModalContent>
          </Zoom>
        </Modal>
      </Box>
    </Box>
  );
};

export default DisclaimersTable;
